// Mirrors the JSON contracts in docs/04-api-specification.md.

export type Stage = 'lead' | 'qualified' | 'proposal' | 'closed';
export type ClosedOutcome = 'won' | 'lost';
export type ActionType = 'call' | 'email' | 'meeting' | 'note' | 'task';
export type PartnerRelationshipType = 'reseller' | 'referral' | 'systemsIntegrator' | 'vendor';

export const PARTNER_RELATIONSHIP_TYPES: PartnerRelationshipType[] =
  ['reseller', 'referral', 'systemsIntegrator', 'vendor'];

export const STAGE_ORDER: Stage[] = ['lead', 'qualified', 'proposal', 'closed'];

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ApiErrorDetail {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: ApiErrorDetail[] | null;
}

export interface CustomerSummary {
  id: number;
  name: string;
  country: string | null;
  emailDomain: string | null;
  openOpportunityCount: number;
  updatedAtUtc: string;
  partnerId: number | null;
  partnerName: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
}

export interface Customer extends CustomerSummary {
  website: string | null;
  notes: string | null;
  createdAtUtc: string;
  createdByName: string;
  updatedByName: string;
}

/** Owner is absent on purpose: it is only writable through the owner endpoint, so the stored
 *  name always comes from the directory (ADR-73). */
export interface CustomerWrite {
  name: string;
  website: string | null;
  country: string | null;
  notes: string | null;
  partnerId: number | null;
}

export interface Contact {
  id: number;
  customerId: number;
  name: string;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  notes: string | null;
  updatedAtUtc: string;
}

export interface ContactWrite {
  customerId: number;
  name: string;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  notes: string | null;
}

export interface Opportunity {
  id: number;
  customerId: number;
  customerName: string;
  title: string;
  stage: Stage;
  closedOutcome: ClosedOutcome | null;
  lostReason: string | null;
  pain: string | null;
  vision: string | null;
  value: string | null;
  estimatedValue: number | null;
  currency: string;
  expectedCloseDate: string | null;
  probability: number;
  championContactId: number | null;
  championName: string | null;
  economicBuyerContactId: number | null;
  economicBuyerName: string | null;
  source: string;
  ownerUserId: string | null;
  ownerName: string | null;
  nextStep: string | null;
  nextStepDueDate: string | null;
  partnerId: number | null;
  partnerName: string | null;
  partnerRelationshipType: PartnerRelationshipType | null;
  partnerMarginPercent: number | null;
  partnerIsInherited: boolean;
  closedAtUtc: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface OpportunityWrite {
  customerId: number;
  title: string;
  pain: string | null;
  vision: string | null;
  value: string | null;
  estimatedValue: number | null;
  currency: string | null;
  expectedCloseDate: string | null;
  probability: number | null;
  championContactId: number | null;
  economicBuyerContactId: number | null;
  source: string | null;
  ownerUserId: string | null;
  nextStep: string | null;
  nextStepDueDate: string | null;
  partnerId: number | null;
  partnerRelationshipType: PartnerRelationshipType | null;
  partnerMarginPercent: number | null;
}

export interface StageMove {
  toStage: Stage;
  closedOutcome: ClosedOutcome | null;
  lostReason: string | null;
  probability?: number | null;
}

export interface StageHistoryEntry {
  fromStage: Stage | null;
  toStage: Stage;
  closedOutcome: ClosedOutcome | null;
  changedAtUtc: string;
  changedByName: string;
}

export interface BoardCard {
  id: number;
  title: string;
  customerId: number;
  customerName: string;
  estimatedValue: number | null;
  currency: string;
  ownerUserId: string | null;
  ownerName: string | null;
  nextStep: string | null;
  nextStepDueDate: string | null;
  isNextStepOverdue: boolean;
  closedOutcome: ClosedOutcome | null;
  partnerId: number | null;
  partnerName: string | null;
  stageEnteredAtUtc: string | null;
  daysInStage: number | null;
}

export interface BoardColumn {
  stage: Stage;
  totalValueByCurrency: Record<string, number>;
  weightedValueByCurrency: Record<string, number>;
  cards: BoardCard[];
}

export interface Board {
  columns: BoardColumn[];
}

export interface ActionItem {
  id: number;
  customerId: number;
  opportunityId: number | null;
  type: ActionType;
  subject: string;
  notes: string | null;
  dueAtUtc: string | null;
  completedAtUtc: string | null;
  outcome: string | null;
  /** When the activity happened or is due — server-computed, and what timelines order by. */
  activityAtUtc: string;
  createdAtUtc: string;
  createdByUserId: string;
  createdByName: string;
}

export interface ActionWrite {
  customerId: number;
  opportunityId: number | null;
  type: ActionType;
  subject: string;
  notes: string | null;
  dueAtUtc: string | null;
  completedAtUtc: string | null;
  outcome: string | null;
}

export interface MyAction {
  id: number;
  type: ActionType;
  subject: string;
  dueAtUtc: string | null;
  isOverdue: boolean;
  customerId: number;
  customerName: string;
  opportunityId: number | null;
  opportunityTitle: string | null;
}

export interface MyNextStep {
  opportunityId: number;
  opportunityTitle: string;
  customerId: number;
  customerName: string;
  nextStep: string | null;
  nextStepDueDate: string | null;
  isOverdue: boolean;
  stage: Stage;
}

export interface MyWork {
  actions: MyAction[];
  nextSteps: MyNextStep[];
}

export interface SearchResult {
  customers: { id: number; name: string; country: string | null }[];
  contacts: { id: number; name: string; email: string | null; customerId: number; customerName: string }[];
  opportunities: { id: number; title: string; stage: Stage; customerId: number; customerName: string }[];
}

export interface Meta {
  stages: { id: Stage; name: string; defaultProbability: number }[];
  actionTypes: ActionType[];
  defaultCurrency: string;
  currencies: string[];
  partnerRelationshipTypes: PartnerRelationshipType[];
}

export interface Partner {
  id: number;
  name: string;
  defaultRelationshipType: PartnerRelationshipType;
  defaultMarginPercent: number | null;
  website: string | null;
  notes: string | null;
  customerCount: number;
  openOpportunityCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  createdByName: string;
  updatedByName: string;
}

export interface PartnerWrite {
  name: string;
  defaultRelationshipType: PartnerRelationshipType;
  defaultMarginPercent: number | null;
  website: string | null;
  notes: string | null;
}

export interface User {
  userId: string;
  name: string;
  email: string | null;
  isActive: boolean;
  lastSeenAtUtc: string;
}

export interface HistoricalOpportunity {
  id: number;
  title: string;
  customerId: number;
  customerName: string;
  closedOutcome: ClosedOutcome | null;
  lostReason: string | null;
  estimatedValue: number | null;
  currency: string;
  closedAtUtc: string | null;
  ownerName: string | null;
  partnerId: number | null;
  partnerName: string | null;
}

export interface HistoricalQuery {
  outcome?: ClosedOutcome;
  q?: string;
  page: number;
}

export interface BoardFilters {
  ownerUserId?: string;
  partnerId?: number;
}
